import styled, { css } from 'styled-components';
import { ANIMATION_CURVE, getSpacingToken, getToken } from '@optimus-web/common';
import { Stack } from '../Stack';
import { getCheckboxToken } from '../Checkbox/tokens';

/**
 * Presentational Checkbox
 *
 * This is a non-interactive visual clone of the main Checkbox component.
 * It's specifically designed for use within DropdownItem components to avoid
 * accessibility issues when nesting interactive elements (like checkboxes)
 * inside elements with role="option".
 *
 * This component mimics the exact visual appearance of the real Checkbox component
 * but without any interactive behavior or keyboard functionality.
 * The containing DropdownItem handles all interaction logic.
 */

const TRANSITION_DURATION = '90ms';
const INDETERMINATE_LINE_WIDTH = 10;
const INDETERMINATE_LINE_HEIGHT = 2;
const INDETERMINATE_LINE_RADIUS = 1;

const getCheckboxSize = (props: CheckboxProps) => getSpacingToken(props.size === 'large' ? '300' : '200');
const getCheckboxState = (checked?: boolean, indeterminate?: boolean): 'checked' | 'mixed' | 'unchecked' => {
    if (checked) return 'checked';
    if (indeterminate) return 'mixed';
    return 'unchecked';
};

interface CheckboxProps {
    checked?: boolean;
    disabled?: boolean;
    indeterminate?: boolean;
    size?: 'medium' | 'large';
}

const CheckboxBackground = styled.div<CheckboxProps>`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${getCheckboxSize}px;
    height: ${getCheckboxSize}px;
    min-width: ${getCheckboxSize}px;

    &::before {
        content: '';
        background-color: ${getCheckboxToken('backgroundColor')};
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        border: ${getToken('borderWidth150')}px solid ${getCheckboxToken('borderColor')};
        border-radius: ${({ size }) => getToken(size === 'large' ? 'borderRadius50' : 'borderRadius25')}px;
        box-sizing: border-box;
        transition:
            border-color ${TRANSITION_DURATION} ${ANIMATION_CURVE.fastOutLinearIn},
            background-color ${TRANSITION_DURATION} ${ANIMATION_CURVE.fastOutLinearIn};
    }

    ${({ disabled, indeterminate }) =>
        indeterminate &&
        css`
            &::after {
                content: '';
                position: absolute;
                width: ${INDETERMINATE_LINE_WIDTH}px;
                height: ${INDETERMINATE_LINE_HEIGHT}px;
                left: calc(50% - ${INDETERMINATE_LINE_WIDTH / 2}px);
                top: calc(50% - ${INDETERMINATE_LINE_HEIGHT / 2}px);
                background-color: ${getToken(disabled ? 'textDisabled' : 'textStaticInverse')};
                border-radius: ${INDETERMINATE_LINE_RADIUS}px;
                z-index: 2;
            }
        `}
`;

const CheckmarkSvg = styled.svg<Pick<CheckboxProps, 'checked' | 'indeterminate'>>`
    opacity: 0;
    transition: opacity ${TRANSITION_DURATION} ${ANIMATION_CURVE.linearOutSlowIn};
    position: relative;
    z-index: 1;

    ${({ checked, indeterminate }) =>
        checked &&
        !indeterminate &&
        css`
            opacity: ${getToken('opacity1000')};
        `}
`;

const CheckmarkPath = styled.path<Pick<CheckboxProps, 'disabled'>>`
    fill: ${({ disabled }) => getToken(disabled ? 'textDisabled' : 'textStaticInverse')};
`;

export const Checkbox: React.FC<CheckboxProps> = ({ checked, indeterminate, disabled, size = 'medium' }) => {
    const dimensions = {
        width: size === 'large' ? '15' : '10',
        height: size === 'large' ? '12' : '8',
    };

    const checkboxState = getCheckboxState(checked, indeterminate);

    return (
        <Stack data-test-dropdown-item-checkbox data-test-dropdown-item-checkbox-state={checkboxState}>
            <CheckboxBackground size={size} disabled={disabled} checked={checked} indeterminate={indeterminate}>
                <CheckmarkSvg
                    checked={checked}
                    indeterminate={indeterminate}
                    width={dimensions.width}
                    height={dimensions.height}
                    viewBox="0 0 10 8"
                    fill="none"
                >
                    <CheckmarkPath
                        disabled={disabled}
                        d="M8.23196 0.361083C8.58565 -0.0630916 9.21623 -0.120233 9.64041 0.233454C10.032 0.559934 10.1108 1.12235 9.84238 1.54057L9.76804 1.6419L4.7685 7.63781C4.41883 8.05716 3.80491 8.11368 3.38616 7.78665L3.2932 7.70436L0.292741 4.70261C-0.0976991 4.312 -0.097563 3.67884 0.293045 3.2884C0.653607 2.92799 1.22084 2.90038 1.61307 3.20549L1.70726 3.2887L3.933 5.51508L8.23196 0.361083Z"
                    />
                </CheckmarkSvg>
            </CheckboxBackground>
        </Stack>
    );
};
